import { Button, DialogContent, DialogTitle, Divider, IconButton, Stack, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { Project } from '../../Projects/projects';
import CopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import DownloadIcon from '@mui/icons-material/Download';
import { useState } from 'react';
import BasicSnackBar from '../../Snackbars/BasicSnackBar';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDispatch } from 'react-redux';
import { applicationSlice } from '../../../redux/ApplicationSlice';
import { ApplicationActions } from '../../../utils/constants';
import { Document, Page, Text, View, StyleSheet, pdf } from '@react-pdf/renderer';
import saveAs from 'file-saver';

interface ShareCodeInfoDialogProps {
  onClose: () => void;
  open: boolean;
  project: Project | null;
  newShareCode: boolean;
}

const ShareCodeInfoDialog = ({ onClose, project, newShareCode }: ShareCodeInfoDialogProps) => {
  const [openCopiedSnackbar, setOpenCopiedSnackbar] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Generate URLs from project data
  const shareCode = project?.shareCode || '';
  const collaboratorPin = project?.pin || '';
  const shareUrl = shareCode ? `https://bps.sphero.com/share/${shareCode}` : '';

  const handleRemoveShareCode = () => {
    onClose();
    dispatch(
      applicationSlice.actions.onOpenDialog({
        ...ApplicationActions.Dialogs.REMOVE_SHARE_CODE,
        options: { ...ApplicationActions.Dialogs.REMOVE_SHARE_CODE.options, project: project },
      })
    );
  };

  const copyToClipboard = (text: string) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).catch((err) => {
        console.error('Failed to copy text: ', err);
      });
      setOpenCopiedSnackbar(true);
    } else {
      // Fallback for very old browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed'; // avoid scrolling to bottom
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand('copy');
        setOpenCopiedSnackbar(true);
      } catch (err) {
        console.error('Fallback: Copy command failed', err);
      }

      document.body.removeChild(textArea);
    }
  };

  const isChrome = (): boolean => {
    const userAgent = navigator.userAgent;
    return /Chrome/.test(userAgent) && !/Edge|Edg|OPR|Opera/.test(userAgent);
  };

  const handleShareUrlEmail = (url: string): void => {
    const subject = encodeURIComponent(t('shareCodeInfoDialog.email.subject', 'Check out this BPS project!'));
    const body = encodeURIComponent(t('shareCodeInfoDialog.email.body', `Here's the link to the BPS project: {{url}}`, { url }));

    if (isChrome()) {
      const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&su=${subject}&body=${body}`;
      window.open(gmailUrl, '_blank', 'noopener,noreferrer,width=600,height=700');
    }

    // Default to mailto
    const mailtoUrl = `mailto:?subject=${subject}&body=${body}`;
    window.location.href = mailtoUrl;
  };

  const handleCollaboratorPinDownload = async () => {
    const blob = await pdf(
      <CollaboratorPinPDF
        projectName={projectName!}
        collaboratorPin={collaboratorPin}
        importantText={t('shareCodeInfoDialog.pdf.important-text', "Don't lose this collaborator PIN, keep it safe!")}
        collaboratorPinText={t('shareCodeInfoDialog.pdf.collaborator-pin', 'Collaborator PIN:')}
        explainText={t(
          'shareCodeInfoDialog.pdf.collaborator-pin-explain',
          'The collaborator PIN allows you (or anyone you share it with) to edit the project on the platform, including removing it entirely from the platform.'
        )}
      />
    ).toBlob();

    const fileName = `${projectName}-collaborator-pin.pdf`;

    saveAs(blob, fileName); // FileSaver triggers download
  };

  const handleCloseCopiedSnackbar = () => {
    setOpenCopiedSnackbar(false);
  };

  const projectName = project?.name;

  return (
    <>
      <DialogTitle textAlign="center">
        {!newShareCode ? (
          <Trans i18nKey={'shareCodeInfoDialog.info.title'}>Share Code information for {{ projectName }} </Trans>
        ) : (
          <Trans i18nKey={'shareCodeInfoDialog.created.title'}>{{ projectName }} is now available to share!</Trans>
        )}
      </DialogTitle>
      <DialogContent sx={{ height: 350, overflow: 'hidden' }}>
        <Stack direction="column" spacing={2} marginTop={1}>
          <Stack direction={'row'} spacing={1} pl={10} alignItems={'center'}>
            <Typography sx={{ pr: 1 }}>
              <Trans i18nKey={'shareCodeInfoDialog.share-code'}>Share Code:</Trans>
            </Typography>
            <Typography variant={'h4'} sx={{ textDecoration: 'underline' }}>
              {shareCode}
            </Typography>
            <IconButton onClick={() => copyToClipboard(shareCode)}>
              <CopyIcon />
            </IconButton>
          </Stack>
          <Stack direction={'row'} px={10} alignItems={'center'}>
            <Stack>
              <Typography sx={{ pr: 1 }}>
                <Trans i18nKey={'shareCodeInfoDialog.share-url'}>Direct Share URL:</Trans>
              </Typography>
              <Typography sx={{ pr: 1 }}>{shareUrl}</Typography>
            </Stack>
            <IconButton onClick={() => copyToClipboard(shareUrl)}>
              <CopyIcon />
            </IconButton>
            <IconButton onClick={() => handleShareUrlEmail(shareUrl)} sx={{ pr: 2 }}>
              <EmailIcon />
            </IconButton>
          </Stack>
          <Divider />
          <Stack direction={'row'} pl={10} alignItems={'center'}>
            <Typography sx={{ pr: 1 }}>
              <Trans i18nKey={'shareCodeInfoDialog.collaborator-pin'}>Collaborator PIN:</Trans>
            </Typography>
            <Typography variant="h4">{collaboratorPin}</Typography>
            <IconButton onClick={handleCollaboratorPinDownload}>
              <DownloadIcon />
            </IconButton>
          </Stack>
          <Typography>
            <Trans i18nKey={'shareCodeInfoDialog.collaborator-pin-explain'}>
              The collaborator PIN allows you (or anyone you share it with) to edit the project on the platform, including removing it entirely from
              the platform. Don't lose it, and share it wisely!
            </Trans>
          </Typography>
          <Stack direction="row" display="flex" position={'relative'} width={'100%'} height={'100%'}>
            {!newShareCode && (
              <Button onClick={handleRemoveShareCode} startIcon={<DeleteIcon />} sx={{ position: 'absolute', left: 0, bottom: -45 }}>
                <Trans i18nKey="shareCodeInfoDialog.button.remove-share-code">Remove Share Code</Trans>
              </Button>
            )}
            <Button onClick={onClose} sx={{ position: 'absolute', right: 0, bottom: -45 }}>
              <Trans i18nKey="openShareCodeDialog.button.close">Close</Trans>
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
      <BasicSnackBar
        message={t('shareCodeInfoDialog.snackbar.copied-to-clipboard', 'Copied to Clipboard')}
        vertical="bottom"
        horizontal="center"
        open={openCopiedSnackbar}
        onClose={handleCloseCopiedSnackbar}
      />
    </>
  );
};

interface CollaboratorPinPDFProps {
  projectName: string;
  collaboratorPin: string;
  explainText: string;
  importantText: string;
  collaboratorPinText: string;
}

const CollaboratorPinPDF = ({ projectName, collaboratorPin, explainText, importantText, collaboratorPinText }: CollaboratorPinPDFProps) => (
  <Document>
    <Page size="A4" style={pdfStyles.page}>
      <Text style={pdfStyles.title}>Project: {projectName}</Text>
      <View style={pdfStyles.section}>
        <Text style={pdfStyles.paragraph}>{explainText}</Text>

        <View style={pdfStyles.pinRow}>
          <Text>{collaboratorPinText}</Text>
          <Text style={pdfStyles.collaboratorPin}>{collaboratorPin}</Text>
        </View>

        <Text style={pdfStyles.paragraph}>{importantText}</Text>
      </View>
    </Page>
  </Document>
);

const pdfStyles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 12,
    fontFamily: 'Helvetica',
  },
  section: {
    marginTop: 20,
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 20,
  },
  paragraph: {
    marginBottom: 12,
  },
  pinRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  collaboratorPin: {
    fontSize: 18,
    fontWeight: 'bold',
    marginLeft: 4,
  },
});

export default ShareCodeInfoDialog;
